import { formatDisplayName } from '../shared/displayName.js';

function isPlainObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function inferSchemaNode(value, title, seen) {
    const node = { readonly: true };
    if (title)
        node.title = title;

    if (typeof value === 'boolean') {
        node.type = 'boolean';
        return node;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        node.type = 'number';
        return node;
    }
    if (Array.isArray(value)) {
        node.type = 'array';
        node.items = inferArrayItemSchema(value, seen);
        return node;
    }
    if (isPlainObject(value)) {
        node.type = 'object';
        node.additionalProperties = false;
        node.properties = inferObjectProperties(value, seen);
        return node;
    }

    node.type = 'string';
    return node;
}

function inferArrayItemSchema(items, seen) {
    const sample = items.find((item) => item != null);
    if (sample == null)
        return { type: 'string', readonly: true };

    if (!isPlainObject(sample))
        return inferSchemaNode(sample, '', seen);

    const merged = {};
    for (const item of items) {
        if (!isPlainObject(item))
            continue;
        for (const key of Object.keys(item)) {
            if (!(key in merged))
                merged[key] = item[key];
        }
    }
    return inferSchemaNode(merged, '', seen);
}

function inferObjectProperties(obj, seen) {
    const properties = {};
    if (!isPlainObject(obj) || seen.has(obj))
        return properties;
    seen.add(obj);
    for (const key of Object.keys(obj))
        properties[key] = inferSchemaNode(obj[key], formatDisplayName(key), seen);
    return properties;
}

function sectionSchema(title, value, seen) {
    if (!isPlainObject(value) || !Object.keys(value).length)
        return null;
    return {
        type: 'object',
        readonly: true,
        title,
        additionalProperties: false,
        properties: inferObjectProperties(value, seen),
    };
}

/**
 * JSON schema for a palette tool that is no longer available.
 * Field types come from the saved model values. Every field is read-only.
 * @param {object} model  { API, Arguments, Output, MatchPorts }
 */
export function inferUnavailableToolSchema(model) {
    const seen = new WeakSet();
    const properties = {
        API: {
            type: 'object',
            readonly: true,
            additionalProperties: false,
            properties: {
                Name: {
                    type: 'string',
                    readonly: true,
                    title: 'Name',
                    condition: 'IsAdvanced',
                },
                Caption: {
                    type: 'string',
                    readonly: true,
                    title: 'Caption',
                },
            },
        },
    };

    const argumentsSchema = sectionSchema('Arguments', model?.Arguments, seen);
    if (argumentsSchema)
        properties.Arguments = argumentsSchema;

    const outputSchema = sectionSchema('Output', model?.Output, seen);
    if (outputSchema)
        properties.Output = outputSchema;

    const matchSchema = sectionSchema('Match ports', model?.MatchPorts, seen);
    if (matchSchema)
        properties.MatchPorts = matchSchema;

    return {
        type: 'object',
        description: 'This tool is not in the current palette. Values are read-only.',
        additionalProperties: false,
        properties,
    };
}
